/**
 * Botón base de la aplicación.
 *
 * Existe para que no se repita en cada pantalla la misma tira de clases y, sobre
 * todo, para fijar dos cosas que en un juego móvil se olvidan y se pagan caras:
 * el área táctil mínima (44 px, la recomendación de Apple y Google) y un estado
 * de foco visible para quien navegue con teclado.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-linear-to-b from-amber-400 to-amber-600 text-amber-950 hover:from-amber-300 hover:to-amber-500 shadow-lg shadow-amber-900/40',
  secondary: 'bg-slate-700 text-slate-100 hover:bg-slate-600 border border-slate-500/60',
  ghost: 'bg-transparent text-slate-300 hover:bg-white/10',
  danger: 'bg-rose-600 text-white hover:bg-rose-500',
};

const SIZES: Record<Size, string> = {
  sm: 'min-h-9 px-3 text-xs',
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-12 px-6 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={[
        'inline-flex items-center justify-center gap-2 rounded-lg font-bold tracking-wide transition',
        'focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
}
